'use client';

import { useState, useEffect } from 'react';

export default function CookieBanner() {
    const [show, setShow] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);

        // Check if user has given consent
        const hasConsent = getCookieValue('cookieConsent');

        console.log('Current cookies:', document.cookie);
        console.log('Has consent:', hasConsent);

        // Show banner if no consent decision has been made
        if (hasConsent === null) {
            setShow(true);
        }

        // If user previously consented, ensure tracking is enabled
        if (hasConsent === 'true') {
            enableTracking();
        }

        // If user previously declined, ensure tracking is disabled
        if (hasConsent === 'false') {
            disableTracking();
        }
    }, []);

    // Helper function to get cookie value
    const getCookieValue = (name) => {
        if (typeof document === 'undefined') return null;

        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        return null;
    };

    const enableTracking = () => {
        if (typeof window !== 'undefined') {
            // Enable Google Analytics if available
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'granted',
                    'ad_storage': 'granted',
                    'functionality_storage': 'granted',
                    'personalization_storage': 'granted',
                    'security_storage': 'granted'
                });
                console.log('Google Analytics consent granted');
            }

            // Enable Tally.so tracking/analytics if they have any
            if (window.Tally && window.Tally.setConsent) {
                window.Tally.setConsent(true);
                console.log('Tally.so consent granted');
            }

            // Set a flag that other parts of your app can check
            window.cookieConsentGranted = true;

            // Dispatch custom event for other scripts to listen to
            window.dispatchEvent(new CustomEvent('cookieConsentGranted', {
                detail: { consent: true }
            }));

            console.log('All tracking enabled');
        }
    };

    const disableTracking = () => {
        if (typeof window !== 'undefined') {
            // Disable Google Analytics if available
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'denied',
                    'ad_storage': 'denied',
                    'functionality_storage': 'denied',
                    'personalization_storage': 'denied'
                });
                console.log('Google Analytics consent denied');
            }

            // Disable Tally.so tracking/analytics if they have any
            if (window.Tally && window.Tally.setConsent) {
                window.Tally.setConsent(false);
                console.log('Tally.so consent revoked');
            }

            // Set a flag that other parts of your app can check
            window.cookieConsentGranted = false;

            // Dispatch custom event for other scripts to listen to
            window.dispatchEvent(new CustomEvent('cookieConsentRevoked', {
                detail: { consent: false }
            }));

            console.log('All tracking disabled');
        }
    };

    const acceptCookies = () => {
        // Set our custom consent cookie
        const expirationDate = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 1);

        document.cookie = `cookieConsent=true; path=/; expires=${expirationDate.toUTCString()}; SameSite=Lax; Secure`;

        // Enable tracking
        enableTracking();

        console.log('Cookies accepted. All tracking enabled.');
        setShow(false);
    };

    const declineCookies = () => {
        // Set decline cookie
        const expirationDate = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 1);

        document.cookie = `cookieConsent=false; path=/; expires=${expirationDate.toUTCString()}; SameSite=Lax; Secure`;

        // Disable tracking
        disableTracking();

        console.log('Cookies declined. Tracking disabled.');
        setShow(false);
    };

    // Don't render on server side
    if (!mounted) return null;

    // Don't show if consent decision already made
    const hasConsent = getCookieValue('cookieConsent');
    if (hasConsent !== null) {
        return null;
    }

    return (
        <>
            {show && (
                <div className="cookie-banner">
                    <div className="cookie-banner__content">
                        <div className="cookie-banner__text">
                            <h3>Ми використовуємо кукі 🍪</h3>
                            <p>
                                Ми використовуємо файли cookie для покращення досвіду користувача та аналітики.
                            </p>
                        </div>
                        <div className="cookie-banner__buttons">
                            <button className="cookie-banner__button cookie-banner__button--decline" onClick={declineCookies}>
                                Відхилити
                            </button>
                            <button className="cookie-banner__button cookie-banner__button--accept" onClick={acceptCookies}>
                                Прийняти всі
                            </button>
                        </div>
                    </div>

                    <style jsx>{`
                        .cookie-banner {
                            position: fixed;
                            bottom: 32px;
                            left: 0;
                            right: 0;
                            display: flex;
                            justify-content: center;
                            z-index: 9999;
                            animation: slideUp 0.5s ease-out;
                            pointer-events: none;
                        }

                        .cookie-banner__content {
                            width: 1144px;
                            background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
                            border: 1px solid #404040;
                            border-radius: 48px;
                            padding: 32px 32px;
                            box-shadow: 0 16px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05);
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            pointer-events: all;
                            box-sizing: border-box;
                            gap: 32px;
                            backdrop-filter: blur(10px);
                        }

                        .cookie-banner__text {
                            flex: 1;
                            max-width: 70%;
                        }

                        .cookie-banner__text h3 {
                            font-size: 20px;
                            margin: 0 0 8px 0;
                            font-weight: 600;
                            color: #fff;
                        }

                        .cookie-banner__text p {
                            margin: 0;
                            line-height: 1.5;
                            color: #e0e0e0;
                            font-size: 18px;
                        }

                        .cookie-banner__buttons {
                            display: flex;
                            gap: 12px;
                            white-space: nowrap;
                            flex-shrink: 0;
                        }

                        .cookie-banner__button {
                            font-size: 18px;
                            font-weight: 600;
                            border: none;
                            height: 48px;
                            padding: 12px 24px;
                            border-radius: 16px;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            position: relative;
                            overflow: hidden;
                        }

                        .cookie-banner__button--accept {
                            background: #FF6F20;
                            color: white;
                            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                        }

                        .cookie-banner__button--accept:hover {
                            transform: scale(1.05);
                            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.75);
                        }

                        .cookie-banner__button--accept:active {
                            transform: translateY(0);
                        }

                        .cookie-banner__button--decline {
                            background: transparent;
                            color: #ccc;
                            border: 2px solid #555;
                        }

                        .cookie-banner__button--decline:hover {
                            background: #555;
                            color: white;
                            border-color: #666;
                            transform: scale(1.05);
                        }

                        @media (max-width: 1144px) {
                            .cookie-banner__content {
                                width: 90%;
                            }
                        }

                        @media (max-width: 767px) {
                            .cookie-banner {
                                bottom: 16px;
                            }

                            .cookie-banner__content {
                                width: 94%;
                                padding: 16px;
                                border-radius: 16px;
                                flex-direction: column;
                                align-items: stretch;
                                gap: 20px;
                            }

                            .cookie-banner__text {
                                max-width: 100%;
                            }

                            .cookie-banner__text h3 {
                                font-size: 18px;
                                text-align: center;
                            }

                            .cookie-banner__text p {
                                text-align: center;
                                font-size: 18px;
                            }

                            .cookie-banner__buttons {
                                flex-direction: column;
                                gap: 12px;
                            }

                            .cookie-banner__button {
                                width: 100%;
                                padding: 16px;
                                font-size: 16px;
                            }
                        }

                        @media (max-width: 480px) {
                            .cookie-banner__content {
                                width: 94%;
                                padding: 16px;
                                gap: 16px;
                            }

                            .cookie-banner__text h3 {
                                font-size: 18px;
                            }

                            .cookie-banner__text p {
                                font-size: 18px;
                            }

                            .cookie-banner__buttons {
                                gap: 8px;
                            }

                            .cookie-banner__button {
                                padding: 16px;
                                font-size: 14px;
                            }
                        }

                        @keyframes slideUp {
                            from {
                                opacity: 0;
                                transform: translateY(100px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                    `}</style>
                </div>
            )}
        </>
    );
}